import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, TrendingUp, Banknote, Percent, Loader2, HandCoins, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * DriverWalletPanel — shows a driver their earnings, tips, and cash-on-hand.
 * Also owns the one-tap "Cash settled" handoff button.
 */
export default function DriverWalletPanel({ apiClient }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get("/driver/wallet");
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);

  const settleCash = async () => {
    if (!window.confirm(`Confirm end-of-shift cash handoff of NZ$${totals.cash_on_hand.toFixed(2)}?`)) return;
    setSettling(true);
    try {
      const { data } = await apiClient.post("/driver/wallet/settle-cash");
      toast.success(`Handed off NZ$${data.amount_settled.toFixed(2)} to the office 👍`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Settle failed");
    } finally { setSettling(false); }
  };

  if (loading && !data) {
    return (
      <div className="glass-card rounded-2xl p-5 gold-border flex items-center gap-2 text-slate-400 text-sm" data-testid="driver-wallet-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading wallet…
      </div>
    );
  }
  const totals = data?.totals || { earnings: 0, tips: 0, commission: 0, cash_collected: 0, cash_settled: 0, cash_on_hand: 0, rides: 0 };
  const txns = data?.transactions || [];

  return (
    <div className="glass-card rounded-2xl p-5 gold-border" data-testid="driver-wallet">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-amber-400/10 border border-amber-400/20 grid place-items-center">
            <Wallet className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <div className="text-caption">Wallet</div>
            <h3 className="font-display font-bold">Your earnings</h3>
          </div>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] font-mono uppercase tracking-widest text-amber-400 hover:text-amber-300"
          data-testid="driver-wallet-toggle"
        >
          {expanded ? "Hide" : "Show"} history
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <StatCard icon={TrendingUp} label="Net earnings" value={`NZ$ ${totals.earnings.toFixed(2)}`} tone="emerald" testid="wallet-earnings" />
        <StatCard icon={Sparkles} label="Tips earned" value={`NZ$ ${totals.tips.toFixed(2)}`} tone="red" testid="wallet-tips" />
        <StatCard icon={Banknote} label="Cash on hand" value={`NZ$ ${totals.cash_on_hand.toFixed(2)}`} tone="amber" testid="wallet-cash" />
        <StatCard icon={Wallet} label="Completed rides" value={totals.rides} tone="violet" testid="wallet-rides" />
      </div>

      {totals.cash_on_hand > 0 && (
        <div className="mt-3 p-3 rounded-lg bg-amber-400/5 border border-amber-400/20 flex items-center justify-between gap-3" data-testid="wallet-cash-reminder">
          <div className="flex items-start gap-2 text-xs text-amber-200 min-w-0">
            <Banknote className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Cash on hand: <b>NZ$ {totals.cash_on_hand.toFixed(2)}</b>. Settle with the office at end of shift.</span>
          </div>
          <Button
            size="sm" onClick={settleCash} disabled={settling}
            className="btn-gold rounded-full shrink-0"
            data-testid="wallet-settle-cash"
          >
            {settling ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <HandCoins className="w-3.5 h-3.5 mr-1.5" />}
            Cash settled
          </Button>
        </div>
      )}

      {expanded && (
        <div className="mt-4 border-t border-white/5 pt-4" data-testid="wallet-history">
          {txns.length === 0 ? (
            <div className="text-slate-500 text-sm text-center py-6">No transactions yet — complete a ride to see it here.</div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-auto">
              {txns.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 text-xs bg-black/20 border border-white/5 rounded-md px-3 py-2" data-testid={`wallet-txn-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-amber-400 truncate">{t.booking_ref ? `#${t.booking_ref}` : t.note || t.type}</div>
                    <div className="text-slate-500">{new Date(t.at).toLocaleString()} · {t.payment_kind || t.type.replace("_", " ")}</div>
                  </div>
                  <div className={`font-mono font-bold text-sm ${labelClass(t.type)}`}>
                    {formatTxn(t)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function labelClass(type) {
  if (type === "cash_collected") return "text-amber-300";
  if (type === "cash_settled") return "text-blue-300";
  return "text-emerald-300";
}
function formatTxn(t) {
  if (t.type === "cash_collected") return `+cash NZ$${(t.amount || 0).toFixed(2)}`;
  if (t.type === "cash_settled")   return `-settled NZ$${(t.amount || 0).toFixed(2)}`;
  const tip = t.tip ? ` (+tip NZ$${t.tip.toFixed(2)})` : "";
  return `+NZ$${(t.net || 0).toFixed(2)}${tip}`;
}

const TONES = {
  emerald: "bg-emerald-400/5 border-emerald-400/20 text-emerald-300",
  amber: "bg-amber-400/5 border-amber-400/20 text-amber-300",
  red: "bg-red-500/5 border-red-400/30 text-red-300",
  slate: "bg-slate-400/5 border-slate-400/20 text-slate-300",
  violet: "bg-violet-400/5 border-violet-400/20 text-violet-300",
};
const StatCard = ({ icon: Icon, label, value, tone = "slate", testid }) => (
  <div className={`rounded-lg border p-3 ${TONES[tone]}`} data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono opacity-80">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className="mt-1 font-display font-bold text-lg">{value}</div>
  </div>
);
